import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import MainPage from './Pages/MainPage';
import TeacherDashBoard from './Pages/TeacherDashBoard';

import TeacherLogin from './components/LoginModules/TeacherLogin';
import StudentAccessPortal from './Pages/StudentAccessPortal';

import UserProfilePage from "./Pages/UserProfilePage";
import SocialCallbackHandler from './components/SocialCallbackHandler/SocialCallbackHandler';
import TeacherDashboardLanding from './Pages/TeacherDashboardLanding';
//import axios from 'axios';
import api from './components/axios/axiosInstance';

import UnitDetailAssessmentPage from "./components/ManageStudents/StudentDashboard/UnitDetailAssessmentPage"; // adjust path if needed
import StudentDashboard from "./components/ManageStudents/StudentDashboard/StudentDashboard";
import BlogHome from './components/blog/BlogHome';
import BlogEditor from './components/blog/BlogEditor';
import PricingPage from './Pages/Pricing/PricingPage';
import PrivacyPolicy from './mainpagecomponents/Legal/PrivacyPolicy'
import TermsOfUse from '././mainpagecomponents/Legal/TermsOfUse';
import FAQPage from '././Pages/FAQ/FAQPage';
import TicketManagementLanding from './components/admin_pages/TicketManagementLanding';
import { AdminTicketManagement } from './components/admin_pages';
import LotteryManagement from './components/admin_pages/LotteryManagement';
import LotteryManagementLanding from './components/admin_pages/LotteryManagementLanding';

// Component to handle hash scrolling
function ScrollToHash() {
    const location = useLocation();

    useEffect(() => {
        if (location.hash) {
            // Small delay to ensure the component has rendered
            setTimeout(() => {
                const element = document.getElementById(location.hash.substring(1));
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 100);
        }
    }, [location.hash, location.pathname]);

    return null;
}

function App() {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true); // 🛡️ block routes until state is restored

    const handleLogin = (userData) => {
        setIsAuthenticated(true);
        setUser(userData);

        // Return a promise that resolves after state update
        return new Promise(resolve => {
            setTimeout(resolve, 10);
        });
    };
    const handleLogout = async () => {
        try {


            // Step 1: Call backend to blacklist the refresh token
            await api.post(`${process.env.REACT_APP_BACKEND_URL}/auth/logout/`, {}, {
                withCredentials: true
            });

        } catch (err) {

            // Don't block logout on error
        } finally {
            // Step 2: Clear all storage


            // Step 3: Delete cookies (if any were used)
            document.cookie.split(";").forEach((cookie) => {
                const name = cookie.trim().split("=")[0];
                document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
            });

            // Step 4: Reset app state
            setIsAuthenticated(false);
            setUser(null);

            // Step 5: Redirect to main page
            window.location.href = "/";
        }
    };

    // Function to check auth when needed (not on app start)
    const checkAuthStatus = async () => {
        try {
            await api.get(`${process.env.REACT_APP_BACKEND_URL}/auth/csrf/`);
            const res = await api.get(`${process.env.REACT_APP_BACKEND_URL}/auth/me`, {
                withCredentials: true,
            });
            setIsAuthenticated(true);
            setUser(res.data);
            return true;
        } catch (err) {

            setIsAuthenticated(false);
            setUser(null);
            return false;
        }
    };

    // Check auth on app start - be smart about when to check
    useEffect(() => {
        const hasAuthCookies = document.cookie.includes('access_token') || document.cookie.includes('refresh_token');
        const isOnProtectedRoute = ['/dashboard', '/profile', '/admin-dashboard'].some(route =>
            window.location.pathname.startsWith(route)
        );

        if (hasAuthCookies || isOnProtectedRoute) {
            // Only check auth if we have cookies OR user is trying to access protected routes
            checkAuthStatus().finally(() => setLoading(false));
        } else {
            // No cookies and on public route = definitely not logged in, skip auth check
            setIsAuthenticated(false);
            setUser(null);
            setLoading(false);
        }
    }, []);

    // 🧠 Prevent routing logic until we've fully determined auth state
    if (loading) return null;

    return (
        <Router>
            <ScrollToHash />
            <Routes key={isAuthenticated ? "auth" : "guest"}>
                <Route path="/blog" element={<BlogHome />} />
                <Route
                    path="/"
                    element={
                        <MainPage
                            onLogin={handleLogin}
                            isAuthenticated={isAuthenticated}
                            user={user}
                            onLogout={handleLogout}
                        />
                    }
                />
                <Route path="/blog-editor" element={<BlogEditor />} />
                <Route path="/teacher-login" element={<TeacherLogin onLogin={handleLogin} />} />
                <Route path="/dashboard/landing" element={<TeacherDashboardLanding />} />
                <Route path="/social-callback" element={<SocialCallbackHandler onLogin={handleLogin} />} />
                <Route path="/student-access" element={<StudentAccessPortal />} />
                <Route path="/student/subject/:subjectName" element={<UnitDetailAssessmentPage />} />
                <Route path="/student-dashboard" element={<StudentDashboard />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/FAQ" element={<FAQPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfUse />} />
                <Route path="/tickets" element={<TicketManagementLanding onLogin={handleLogin} />} />
                <Route
                    path="/admin-dashboard"
                    element={
                        isAuthenticated ? (
                            <AdminTicketManagement onLogout={handleLogout} />
                        ) : (
                            <TicketManagementLanding onLogin={handleLogin} />
                        )
                    }
                />
                <Route
                    path="/lottery-management"
                    element={
                        isAuthenticated ? (
                            <LotteryManagement onLogout={handleLogout} />
                        ) : (
                            <LotteryManagementLanding onLogin={handleLogin} />
                        )
                    }
                />
                {/* ✅ Protected Routes with Outlet */}
                <Route
                    path="/dashboard"
                    element={
                        isAuthenticated ? (
                            <TeacherDashBoard onLogout={handleLogout} user={user} />
                        ) : (
                            <Navigate to="/" replace />
                        )
                    }
                />
                {/* Protected Routes - Require Authentication */}
                <Route
                    path="/profile"
                    element={
                        isAuthenticated ? (
                            <UserProfilePage />
                        ) : (
                            <Navigate to="/teacher-login" replace />
                        )
                    }
                />
                <Route
                    path="/dashboard/profile"
                    element={
                        isAuthenticated ? (
                            <UserProfilePage />
                        ) : (
                            <Navigate to="/teacher-login" replace />
                        )
                    }
                />

                {/* Fallback */}
                <Route path="*" element={<Navigate to="/" />} />
            </Routes>
        </Router>
    );
}

export default App;
